<template>
  <div class="highlighted-articles">
    <!-- Highlighted article block -->
    <div
      v-if="normalizedHighlight"
      class="highlighted-block relative group"
    >
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
        <div class="col-span-1">
          <img
            v-if="normalizedHighlight.src"
            :src="normalizedHighlight.src"
            :alt="normalizedHighlight.alt"
            class="w-full aspect-video rounded-md object-cover"
          >
        </div>
        <div class="col-span-1">
          <div class="flex flex-col justify-center h-full">
            <span
              v-if="normalizedHighlight.tag"
              class="px-2 py-1 text-sm bg-gray-50 rounded-md w-fit mb-2"
            >
              {{ normalizedHighlight.tag }}
            </span>

            <h3
              v-if="normalizedHighlight.title"
              class="text-black"
            >
              {{ normalizedHighlight.title }}
            </h3>

            <p
              v-if="normalizedHighlight.description"
              class="line-clamp-4"
            >
              {{ normalizedHighlight.description }}
            </p>

            <!-- visible link label -->
            <span
              v-if="normalizedHighlight.linkUrl"
              class="card-link-span group-hover:underline"
            >
              <span class="flex flex-row justify-center w-fit items-center gap-2">
                <ChevronRight class="w-3 h-3 mt-[2px] text-primary-bravo" />
                {{ i18n.cardlabel }}
              </span>
            </span>
          </div>
        </div>
      </div>

      <!-- overlay anchor -->
      <a
        v-if="normalizedHighlight.linkUrl"
        class="highlighted-link"
        :href="normalizedHighlight.linkUrl"
        :target="hasExternalLink(normalizedHighlight.linkUrl) ? '_blank' : null"
        :rel="hasExternalLink(normalizedHighlight.linkUrl) ? 'noopener noreferrer' : null"
        :aria-label="normalizedHighlight.title || normalizedHighlight.description"
      />
    </div>

    <!-- Secondary cards -->
    <div
      v-if="normalizedCards.length"
      class="mt-12"
    >
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-16">
        <KfCard
          v-for="(card, index) in normalizedCards"
          :key="card.id || `${card.title}-${index}`"
          type="secondary"
          :src="card.src"
          :alt="card.alt"
          :link-url="card.linkUrl"
          :description="card.title"
          :centered="card.review"
          :review="card.review || false"
          class="group"
        >
          <template #card-body>
            <span
              v-if="card.tag || card.label"
              class="card-label !mb-4"
            >
              {{ card.tag || card.label }}
            </span>
            <kf-heading
              v-if="card.title"
              tag="h3"
              class="card-title !mb-4"
            >
              {{ card.title }}
            </kf-heading>

            <p
              v-if="card.subtitle"
              class="card-subtitle"
            >
              {{ card.subtitle }}
            </p>

            <p
              v-if="card.description && truncateIntro"
              :class="{ italic: card.descriptionIsQuote }"
              class="line-clamp-2"
            >
              {{ truncate(card.description, 109, '...') }}
            </p>
            <p
              v-else-if="card.description"
              :class="{ italic: card.descriptionIsQuote }"
              class="line-clamp-2"
            >
              {{ card.description }}
            </p>

            <span
              v-if="i18n && i18n.cardlabel"
              class="card-link-span !pl-0 group-hover:underline inline-flex items-center gap-2"
            >
              <span class="flex flex-row justify-center w-fit items-center gap-2">
                <ChevronRight class="w-3 h-3 mt-[2px] text-primary-bravo" />
                {{ i18n.cardlabel }}
              </span>
            </span>
            <span
              v-else-if="card.linkLabel"
              class="card-link-span !pl-0 group-hover:underline inline-flex items-center gap-2"
            >
              <span class="flex flex-row justify-center w-fit items-center gap-2">
                <ChevronRight class="w-3 h-3 mt-[2px] text-primary-bravo" />
                {{ i18n.cardlabel }}
              </span>
            </span>
          </template>
        </KfCard>
      </div>
    </div>
  </div>
</template>

<script>
import KfHeading from '@/components/base/heading/Heading.vue';
import KfCard from '@/components/card/Card.vue';
import ChevronRight from '@/components/svg/ChevronRight.vue';

export default {
  name: 'HighlightedArticles',
  components: {  ChevronRight, KfCard, KfHeading },
  props: {
    i18n: { type: Object, required: true },
    highlightedArticle: { type: Object, default: () => ({}) },
    cards: { type: Array, default: () => [] },
    truncateIntro: { type: Boolean, default: false },
  },
  computed: {
    normalizedHighlight() {
      return this.normalizeCard(this.highlightedArticle) || null;
    },
    normalizedCards() {
      return (this.cards || []).map(this.normalizeCard);
    },
  },
  methods: {
    normalizeCard(card = {}) {
      if (!card || typeof card !== 'object') return null;
      const c = { ...card };
      c.src = c.src || '';
      c.alt = c.alt || c.title || '';
      c.title = c.title || '';
      c.description = c.description || '';
      c.linkUrl = c.linkUrl || '';
      c.linkLabel = c.linkLabel || this.i18n?.cardlabel || '';
      c.tag = c.tag || c.label || '';
      c.subtitle = c.subtitle || '';
      c.review = c.review || false;
      c.descriptionIsQuote = c.descriptionIsQuote || false;
      return c;
    },
    truncate(text, length, suffix = '...') {
      if (!text || typeof text !== 'string') return '';
      if (text.length <= length) return text;
      return text.slice(0, length).trimEnd() + suffix;
    },
    hasExternalLink(url) {
      return /^https?:\/\//.test(url);
    },
  },
};
</script>

<style lang="postcss">
.highlighted-articles {
  @apply space-y-12;
}

.highlighted-link {
  @apply absolute inset-0 block;
  z-index: 10;
}

/* label keep same as cards (but no pseudo icon) */
.card-link-span {
  @apply inline-block mt-4 ml-0 pl-0 pointer-events-none;
  &::before { content: none; }
}

.card-title { @apply text-xl font-semibold text-black mb-2; }
.card-label { @apply text-sm text-gray-500 block mb-1; }
.card-subtitle { @apply text-base text-gray-700 mb-2; }
</style>
